/* eslint-disable max-classes-per-file */
export type Unsubscribe = () => void;

class PubSub<T = unknown, F extends Function = (data: T) => void> {
  private subscribers = new Set<F>();

  sub(fn: F): Unsubscribe {
    this.subscribers.add(fn);

    return () => this.subscribers.delete(fn);
  }

  pub(data: T) {
    this.subscribers.forEach((fn) => fn(data));
  }
}

/**
 * List of valid event args given `K`.
 */
export type EventArg<T, K extends keyof T> = T[K];

/**
 * An event emitter class we can use to manage hooks or other emittable data.
 * Take a type argument of shape Record<eventName, callBackData>
 *
 * A classified version of the MIT licensed https://robinpokorny.github.io/dead-simple/
 * With some type safety inspiration from https://github.com/serviejs/events
 */
export class EventEmitter<T> {
  private events = new Map<keyof T, PubSub>();

  /**
   * Registers a listener that will be called when the named event is emitted
   * @returns An unsubscribe function that will turn off this listener.
   */
  on<K extends keyof T>(name: K, fn: (data: EventArg<T, K>) => void): Unsubscribe {
    if (!this.events.has(name)) this.events.set(name, new PubSub<EventArg<T, K>>());

    return this.events.get(name).sub(fn);
  }

  /**
   * Registers a listener that will be called a single time when the named event
   * is emitted
   * @returns An unsubscribe function that will turn off this listener.
   */
  once<K extends keyof T>(name: K, fn: (data: EventArg<T, K>) => void): Unsubscribe {
    const off = this.on(name, (data: EventArg<T, K>) => {
      off();
      return fn(data);
    });

    return off;
  }

  /**
   * Removes all event listeners for the given key
   * @returns void
   */
  off<K extends keyof T>(name: K) {
    this.events.delete(name);
  }

  /**
   * Used to emit events to all registered hook listeners
   *
   * @private
   */
  emit<K extends keyof T>(name: K, data: EventArg<T, K>) {
    if (this.events.has(name)) {
      this.events.get(name).pub(data);
    }
  }
}
